import { groq } from "next-sanity"

import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getCustomer } from "../../shared/utils/getCustomer"
import { visitReportProjection } from "../sanity/visitReport.projection"
import { visitReportOccasionProjection } from "../sanity/visitReportsOccasion.projection"

export const queryVisitReport = {
  async visitReport(_parent, args, context, _info) {
    const { lang = AppSettings.defaultLocale(), id, cdn = false } = args
    const { user } = context
    const client = context.sanityClient
    client.config({
      ...AppSettings.sanityConfigByLang(lang),
      useCdn: cdn,
    })

    await checkPermissions({
      user,
      domainObject: DomainObjects.VISIT_REPORTS,
      operation: Operations.READ,
    })

    const sanityVisitReport = await getSanityVisitReport(client, user, id)

    if (sanityVisitReport) {
      return sanityVisitReport
    }

    // BSB only provides occasions labels and not the exact object, need to filter them ourselves
    const occasions = await client.fetch(groq`*[_type=="occasion"]{
        ${visitReportOccasionProjection()}
      }`)

    const dbVisitReport = await getDbVisitReport(id, user, occasions)

    if (dbVisitReport) {
      return dbVisitReport
    }

    return null
  },
}

/**
 * Since BSB provides us with invalid visit reports containing empty required fields and wrong data types,
 * we need to add default values to the visitReport object.
 *
 * @param {object} visitReport
 * @param {boolean} readonly
 * @param {array|undefined} occasions
 *
 * @returns {object}
 */
const addDefaultValues = async (visitReport, readonly, occasions) => {
  let selectedOccasions = visitReport.occasions || []
  const attendanceAt = visitReport.attendanceAt ? new Date(visitReport.attendanceAt).toISOString().split("T")[0] : ""

  if (occasions) {
    selectedOccasions = occasions.filter((occasion) => visitReport?.occasions?.includes(occasion?.name))
  }

  const customer = await getCustomer({ number: visitReport?.customerNumber })

  return {
    ...visitReport,
    companyName: customer?.companyName,
    mailRecipients: visitReport.mailRecipients || "",
    mailSubject: visitReport.mailSubject || "",
    text: visitReport.text || "",
    meetingName: visitReport.meetingName || "",
    occasions: selectedOccasions,
    attachments: visitReport.attachments || [],
    attachmentReferences: visitReport.attachmentReferences || [],
    attendanceAt,
    readonly,
  }
}

/**
 * Checks if the date is within the last 3 months
 *
 * @param {string} date
 * @returns {boolean}
 */
const isReportWithinThreeMonths = (date) => {
  if (!date) return false
  const dateString = new Date(date).toISOString()
  const threeMonthsAgo = new Date()
  threeMonthsAgo.setHours(1, 0, 0, 0)
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
  return dateString >= threeMonthsAgo.toISOString()
}

/**
 * Retrieves visit reports from sanity
 * If not found, will attemt to retrieve from db
 *
 * @param {sanityClient} client
 * @param {object} user
 * @param {string} id
 *
 * @returns {Promise<object>}
 */
const getSanityVisitReport = async (client, user, id) => {
  const draftsFilter = `&& !(_id in path("drafts.**"))`

  const visitReport = await client.fetch(
    groq`*[_type=="visitReport" && _id==$id ${draftsFilter}][0] {
      ${visitReportProjection()}
    }`,
    { id: id },
  )
  if (!visitReport) {
    return null
  }

  await validateAccessRightsToCustomer({
    user: user,
    customerNumber: visitReport.customerNumber,
  })

  // If the visit report is older than 3 months, it should be readonly
  const readonly = !isReportWithinThreeMonths(visitReport?.attendanceAt)

  return addDefaultValues(visitReport, readonly)
}

/**
 * If sanity visit report was not found, will attempt to retrieve visit report from DB
 *
 * @param {string} id
 * @param {object} user
 * @param {array} occasions
 *
 * @returns {Promise<object>}
 */
const getDbVisitReport = async (id, user, occasions) => {
  const graphiQLQueryObject = [
    {
      method: "visitReport: findVisitReport",
      variables: {
        sysUuid: id,
      },
      fields: [
        "id: sysUuid",
        "customerNumber",
        "contactName",
        "attendanceStart",
        "attendanceEnd",
        "meetingName",
        "occasions",
        "mailRecipients",
        "mailSubject",
        "text",
        "creationAt",
        "attendanceAt",
        `attachments {
          id: sysUuid
          name
          mimetype :mimeType
        }`,
      ],
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
  const visitReport = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.visitReport

  if (!visitReport?.id) {
    return null
  }

  await validateAccessRightsToCustomer({
    user: user,
    customerNumber: visitReport.customerNumber,
  })

  return addDefaultValues(visitReport, true, occasions)
}
